import { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from '../../components/DatePicker';
import { apiRequest } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import UsageInfo from '../../components/UsageInfo';
import { downloadExcel, printTable } from '../../utils/exportUtils';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import Pagination from '../../components/Pagination';
import ActionButtons, { EditButton, DeleteButton } from '../../components/ActionButtons';

// ── Yardımcılar ───────────────────────────────────────────
function pad(n) { return String(n).padStart(2, '0'); }
function bugun() {
  var d = new Date();
  return pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear();
}
function parseISO(iso) {
  if (!iso) return null;
  if (iso.endsWith('Z') || iso.match(/[+-]\d{2}:\d{2}$/)) return new Date(iso);
  var [dp, tp = '00:00:00'] = iso.split('T');
  var [y, m, g] = dp.split('-').map(Number);
  var [h, mi] = tp.split(':').map(Number);
  return new Date(y, m - 1, g, h, mi);
}
function fmtTarih(iso) {
  if (!iso) return '';
  var d = parseISO(iso);
  return pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear();
}
function ddmmyyyyToDate(s) {
  if (!s) return null;
  var p = s.split('.');
  if (p.length < 3) return null;
  return new Date(Number(p[2]), Number(p[1]) - 1, Number(p[0]));
}
function localISODate(s) {
  var p = s.split('.');
  return p[2] + '-' + p[1].padStart(2, '0') + '-' + p[0].padStart(2, '0') + 'T00:00:00';
}
function fmtPara(val) {
  return (val || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ── Progress Bar Bileşeni ─────────────────────────────────
function ProgressBar({ percent, color = 'bg-blue-600' }) {
  return (
    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
      <div
        className={`h-full ${color} transition-all duration-1000 ease-out rounded-full`}
        style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
      />
    </div>
  );
}

// ── Durum Rozeti Bileşeni ─────────────────────────────────
function StatusBadge({ durum }) {
  var config = {
    'Aktif': 'bg-indigo-50 text-indigo-600 border-indigo-100',
    'İptal': 'bg-rose-50 text-rose-600 border-rose-100',
    'Kapandı': 'bg-emerald-50 text-emerald-600 border-emerald-100'
  };
  return (
    <span className={`px-2.5 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest border ${config[durum] || 'bg-gray-50 text-gray-400'}`}>
      {durum}
    </span>
  );
}

// ── Onay Modalı Bileşeni ──────────────────────────────────
function OnayModal({ baslik, icerik, onOnay, onIptal, tehlikeli = false }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[99999] flex items-center justify-center p-4 animate-fadeIn" onClick={onIptal}>
      <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-sm p-8 flex flex-col items-center gap-6 text-center relative overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className={`absolute top-0 right-0 -mr-10 -mt-10 w-32 h-32 ${tehlikeli ? 'bg-rose-50' : 'bg-blue-50'} rounded-full blur-2xl opacity-60`}></div>
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl shadow-inner relative z-10 ${tehlikeli ? 'bg-rose-100 text-rose-600' : 'bg-blue-100 text-blue-600'}`}>
          {tehlikeli ? '🗑️' : '❓'}
        </div>
        <div className="relative z-10">
          <h3 className="text-lg font-black text-gray-900 uppercase tracking-tighter mb-2">{baslik}</h3>
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest leading-relaxed">{icerik}</p>
        </div>
        <div className="flex gap-3 w-full relative z-10">
          <button onClick={onIptal} className="flex-1 h-12 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95">İptal</button>
          <button onClick={onOnay} className={`flex-1 h-12 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 shadow-lg ${tehlikeli ? 'bg-rose-500 hover:bg-rose-600 shadow-rose-100' : 'bg-blue-600 hover:bg-blue-700 shadow-blue-100'}`}>Onayla</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Mobil Taksit Plan Kartı ───────────────────────────────
function TaksitPlanKart({ p, onDetay, onDuzenle, onSil, onIptal }) {
  var progress = (p.odenenTutar / p.toplamTutar) * 100;
  var isCompleted = progress >= 100;
  var isCancelled = p.planDurum === 'İptal';

  return (
    <div
      className={`group relative overflow-hidden bg-white border-2 rounded-[2.5rem] p-6 transition-all duration-300 shadow-sm hover:shadow-xl hover:-translate-y-1 transform-gpu ${isCancelled ? 'border-rose-100 grayscale-[0.5]' : 'border-gray-100'}`}
    >
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="min-w-0" onClick={() => onDetay(p)}>
          <div className="flex items-center gap-2 mb-1">
            <StatusBadge durum={isCompleted ? 'Kapandı' : p.planDurum} />
          </div>
          <p className="font-black text-gray-900 text-base truncate uppercase tracking-tight">{p.adSoyad}</p>
          <p className="text-[10px] font-bold text-gray-400 mt-0.5 uppercase tracking-widest">Oda {p.odaNo || '—'} · Rez #{p.rezNo}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!isCancelled && <EditButton onClick={() => onDuzenle(p)} />}
          {!isCancelled && <button onClick={() => onIptal(p)} className="px-3 py-1.5 rounded-xl bg-orange-50 hover:bg-orange-500 text-orange-500 hover:text-white text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 border border-orange-100">İptal</button>}
          <DeleteButton onClick={() => onSil(p)} />
        </div>
      </div>

      <div onClick={() => onDetay(p)} className="cursor-pointer">
        <div className="grid grid-cols-2 gap-4 mb-5">
          <div>
            <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Toplam Plan</p>
            <p className="text-sm font-black text-gray-800 tracking-tighter">{fmtPara(p.toplamTutar)} TL</p>
          </div>
          <div className="text-right">
            <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Kalan Borç</p>
            <p className="text-sm font-black text-rose-500 tracking-tighter">{fmtPara(p.kalanTutar)} TL</p>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-[9px] font-black uppercase tracking-widest">
            <span className="text-gray-400">İşlem: {p.odenenTaksitSayisi}/{p.toplamTaksitSayisi}</span>
            <span className="text-blue-600">{progress.toFixed(0)}%</span>
          </div>
          <ProgressBar percent={progress} color={isCompleted ? 'bg-emerald-500' : 'bg-blue-600'} />
        </div>

        <div className="mt-5 pt-4 border-t border-gray-50 flex items-center justify-between">
          <span className="text-[10px] font-black text-gray-300 uppercase tracking-widest">Plan: {p.sure}</span>
          <span className="text-blue-500 text-[10px] font-black uppercase tracking-widest group-hover:translate-x-1 transition-transform">Yönet →</span>
        </div>
      </div>
    </div>
  );
}

// ── Yeni / Düzenle Plan Modal ─────────────────────────────
function YeniPlanModal({ onClose, onKaydedildi, editPlan = null }) {
  var [rezervasyonlar, setRezervasyonlar] = useState([]);
  var [bakiyeMap, setBakiyeMap] = useState({});
  var [secilenRez, setSecilenRez] = useState(null);
  var [toplamTutar, setToplamTutar] = useState(editPlan ? editPlan.toplamTutar.toString() : '');
  var [sure, setSure] = useState(editPlan ? editPlan.sure : 'Aylık');
  var [odemeSayisi, setOdemeSayisi] = useState(editPlan ? editPlan.odemeSayisi : 3);
  var [ilkVade, setIlkVade] = useState(bugun());
  var [girisTarihindeBasla, setGirisTarihindeBasla] = useState(editPlan ? editPlan.girisTarihindeBaşla : false);
  var [kaydediliyor, setKaydediliyor] = useState(false);
  var [hata, setHata] = useState('');
  var [aramaRez, setAramaRez] = useState('');

  // Smart Edit Bilgileri
  var odenenTaksitler = editPlan?.taksitDetaylar?.filter(x => x.odendi) || [];
  var odenmisTutar = odenenTaksitler.reduce((s, x) => s + x.tutar, 0);
  var odenmisSayisi = odenenTaksitler.length;

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  useEffect(() => {
    Promise.all([
      apiRequest('rezervasyonlar'),
      apiRequest('rezervasyonlar/bakiyeler'),
    ]).then(([rezData, bakiyeData]) => {
      var aktif = (rezData || []).filter(r =>
        r.durum === 'Check IN Bekliyor' || r.durum === 'C-IN Yapıldı'
      );
      setRezervasyonlar(aktif);
      var bMap = {};
      (bakiyeData || []).forEach(b => { bMap[b.rezervasyonId] = b.kalanBakiye || 0; });
      setBakiyeMap(bMap);

      if (editPlan) {
        var rez = (rezData || []).find(r => r.rezervasyonId === editPlan.rezervasyonId);
        if (rez) setSecilenRez(rez);

        // Eğer ödenmemiş taksitler varsa, ilk ödenmemişin tarihini vade tarihi yapabiliriz
        var ilkOdenmemis = editPlan.taksitDetaylar?.find(x => !x.odendi);
        if (ilkOdenmemis) setIlkVade(fmtTarih(ilkOdenmemis.vadeTarihi));
      }
    }).catch(() => { });
  }, [editPlan]);

  useEffect(() => {
    if (secilenRez && girisTarihindeBasla && !editPlan) {
      setIlkVade(fmtTarih(secilenRez.girisTarihi));
    }
  }, [secilenRez, girisTarihindeBasla, editPlan]);

  var filtreliRezler = rezervasyonlar.filter(r => {
    if (!aramaRez) return true;
    var ara = aramaRez.toLowerCase();
    var misafir = r.rezervasyonMisafirler?.[0]?.misafir?.adSoyad?.toLowerCase() || '';
    return misafir.includes(ara) || (r.rezNo || '').includes(ara) || (r.odaNo || '').toString().includes(ara);
  });

  // Kalan taksit hesabı
  var kalanTutar = Math.max(0, parseFloat(toplamTutar || 0) - odenmisTutar);
  var kalanTaksitSayisi = Math.max(0, odemeSayisi - odenmisSayisi);
  var taksitTutarVal = kalanTaksitSayisi > 0 ? (kalanTutar / kalanTaksitSayisi).toFixed(2) : '0';

  function handleRezSec(r) {
    if (editPlan) return;
    setSecilenRez(r);
    var kalan = bakiyeMap[r.rezervasyonId];
    setToplamTutar(kalan !== undefined && kalan > 0 ? kalan.toFixed(2) : String(r.odaTutari || ''));
  }

  async function handleKaydet() {
    if (!secilenRez) { setHata('Lütfen bir rezervasyon seçiniz.'); return; }
    if (!toplamTutar || parseFloat(toplamTutar) <= 0) { setHata('Geçerli bir tutar giriniz.'); return; }
    if (parseFloat(toplamTutar) < odenmisTutar) { setHata(`Yeni tutar zaten ödenmiş olan ${fmtPara(odenmisTutar)} TL'den az olamaz.`); return; }
    if (odemeSayisi < odenmisSayisi) { setHata(`Taksit sayısı ödenmiş taksit adedinden (${odenmisSayisi}) az olamaz.`); return; }

    setKaydediliyor(true); setHata('');
    try {
      var payload = {
        rezervasyonId: secilenRez.rezervasyonId,
        toplamTutar: parseFloat(toplamTutar),
        sure,
        odemeSayisi: parseInt(odemeSayisi),
        girisTarihindeBaşla: girisTarihindeBasla,
        ilkVadeTarihi: localISODate(ilkVade),
      };

      if (editPlan) {
        await apiRequest('taksit-planlari/' + editPlan.taksitPlaniId, 'PUT', payload);
      } else {
        await apiRequest('taksit-planlari', 'POST', payload);
      }
      onKaydedildi();
      onClose();
    } catch (e) {
      setHata(e.message || 'Kayıt sırasında hata oluştu.');
    }
    setKaydediliyor(false);
  }

  const INP = "w-full border-2 border-gray-100 rounded-2xl px-5 py-3.5 text-xs font-bold focus:outline-none focus:border-blue-400 bg-white placeholder-gray-300 uppercase tracking-widest transition-colors shadow-sm";
  const LAB = "text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1 px-1";

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-fadeIn" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-2xl rounded-[2.5rem] shadow-2xl flex flex-col relative overflow-hidden" style={{ maxHeight: '92dvh' }} onClick={e => e.stopPropagation()}>

        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-48 h-48 bg-blue-50 rounded-full blur-3xl opacity-60 pointer-events-none"></div>

        <div className="flex items-center justify-between px-4 sm:px-8 py-4 sm:py-6 border-b border-gray-50 flex-shrink-0 relative gap-3">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center text-xl shadow-inner ${editPlan ? 'bg-indigo-100 text-indigo-600' : 'bg-blue-100 text-blue-600'}`}>{editPlan ? '✏️' : '📄'}</div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-black text-gray-900 tracking-tight uppercase leading-none font-mono">{editPlan ? 'Planı Güncelle' : 'Yeni Taksit Planı'}</h2>
              <p className="text-[10px] font-bold text-gray-400 mt-1 uppercase tracking-tight truncate">{editPlan ? 'Kalan tutarı yeniden yapılandır' : 'Ödeme planı ve periyot oluştur'}</p>
            </div>
          </div>
          <button onClick={onClose} className="flex-shrink-0 w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-all text-2xl font-black">✕</button>
        </div>

        <div className="px-4 sm:px-8 py-6 space-y-6 overflow-y-auto flex-1 custom-scrollbar">
          {hata && <div className="bg-rose-50 border-2 border-rose-100 rounded-2xl px-5 py-4 text-[10px] font-black text-rose-600 uppercase tracking-widest animate-shake">⚠️ {hata}</div>}

          {editPlan && odenmisSayisi > 0 && (
            <div className="bg-indigo-50/50 border-2 border-indigo-100 rounded-3xl p-5 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-1">Özet Bilgi</p>
                <p className="text-xs font-bold text-indigo-900 uppercase">Bu planda {odenmisSayisi} taksit ({fmtPara(odenmisTutar)} TL) çoktan tahsil edilmiş.</p>
              </div>
              <div className="text-right">
                <p className="text-[9px] font-black text-indigo-400 uppercase tracking-widest mb-1">Korunan Miktar</p>
                <p className="text-sm font-black text-indigo-600">{fmtPara(odenmisTutar)} TL</p>
              </div>
            </div>
          )}

          <div>
            <label className={LAB}>Rezervasyon {editPlan ? '(Değiştirilemez)' : 'Seç'}</label>
            {!editPlan && (
              <div className="relative group">
                <input value={aramaRez} onChange={e => setAramaRez(e.target.value)}
                  className={INP + " pr-12 group-focus:border-blue-500"} placeholder="Misafir adı, rez. no veya oda ara..." />
                <span className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-300 group-hover:text-blue-400 transition-colors">🔍</span>
              </div>
            )}

            <div className={`mt-4 border-2 border-gray-100 rounded-[2rem] overflow-hidden ${editPlan ? 'bg-blue-50/50' : 'bg-gray-50/30'}`}>
              <div className="max-h-52 overflow-y-auto custom-scrollbar">
                {(editPlan ? [secilenRez] : filtreliRezler).map(r => {
                  if (!r) return null;
                  var misafir = r.rezervasyonMisafirler?.[0]?.misafir?.adSoyad || '—';
                  var secili = secilenRez?.rezervasyonId === r.rezervasyonId;
                  var kalan = bakiyeMap[r.rezervasyonId];
                  return (
                    <div key={r.rezervasyonId} onClick={() => handleRezSec(r)}
                      className={`flex items-center justify-between px-6 py-4 cursor-pointer border-b border-gray-50 last:border-0 transition-all ${secili ? 'bg-blue-600 text-white shadow-xl scale-[0.98] rounded-xl my-1 mx-2' : 'hover:bg-white text-gray-700'
                        } ${editPlan ? 'pointer-events-none' : ''}`}>
                      <div>
                        <p className={`text-sm font-black tracking-tight uppercase leading-none ${secili ? 'text-white' : 'text-gray-900'}`}>{misafir}</p>
                        <p className={`text-[10px] font-bold mt-1 uppercase tracking-widest ${secili ? 'text-blue-100' : 'text-gray-400'}`}>#{r.rezNo} · Oda {r.odaNo || '—'}</p>
                      </div>
                      <div className="text-right flex flex-col items-end gap-1.5">
                        <span className={`text-[9px] font-black px-2.5 py-1 rounded-xl uppercase tracking-widest ${secili ? 'bg-white/20 text-white' : (r.durum === 'C-IN Yapıldı' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700')
                          }`}>
                          {r.durum === 'C-IN Yapıldı' ? 'C-IN' : 'Bekliyor'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={LAB}>Toplam Tutar (TL)</label>
              <input type="number" value={toplamTutar} onChange={e => setToplamTutar(e.target.value)}
                className={INP} placeholder="0.00" />
            </div>
            <div>
              <label className={LAB}>Periyot</label>
              <div className="flex bg-gray-100 p-1 rounded-2xl h-[52px]">
                {['Aylık', 'Haftalık', 'Günlük'].map(s => (
                  <button key={s} onClick={() => setSure(s)}
                    className={`flex-1 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${sure === s ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'
                      }`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
            <div>
              <label className={LAB}>Taksit Sayısı</label>
              <input type="number" min="1" max="120" value={odemeSayisi}
                onChange={e => setOdemeSayisi(Math.max(1, parseInt(e.target.value) || 1))}
                className={INP} />
            </div>
            <div>
              <label className={LAB}>İlk Vade Tarihi</label>
              <DatePicker value={ilkVade} onChange={setIlkVade} />
            </div>
          </div>

          <div className="bg-gray-50 border-2 border-gray-100 rounded-[2rem] p-5 flex items-center justify-between gap-4 group transition-colors hover:border-blue-100">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className={`w-10 h-10 flex-shrink-0 rounded-xl flex items-center justify-center text-lg transition-all shadow-sm ${girisTarihindeBasla ? 'bg-blue-600 text-white' : 'bg-white text-gray-300 border'}`}>📅</div>
              <div className="min-w-0">
                <p className="text-[10px] font-black text-gray-800 uppercase tracking-wider leading-none">Giriş Tarihinde Başlat</p>
                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-1">İlk taksiti giriş tarihine ayarlar</p>
              </div>
            </div>
            <button onClick={() => setGirisTarihindeBasla(!girisTarihindeBasla)}
              disabled={!secilenRez}
              className={`relative flex-shrink-0 w-14 h-8 rounded-full transition-all duration-300 outline-none ${!secilenRez ? 'opacity-30' : ''} ${girisTarihindeBasla ? 'bg-blue-600' : 'bg-gray-200'}`}>
              <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-all duration-300 shadow-sm transform ${girisTarihindeBasla ? 'translate-x-6' : ''}`}></div>
            </button>
          </div>

          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-[2rem] p-6 text-white shadow-xl relative overflow-hidden group">
            <p className="text-[10px] font-black uppercase tracking-widest opacity-70 mb-2">📋 Yeni Plan Özeti</p>
            <div className="space-y-1">
              <p className="text-xl font-black">{odemeSayisi} × {fmtPara(taksitTutarVal)} TL</p>
              <p className="text-[10px] font-bold tracking-widest uppercase opacity-90">= Toplam {fmtPara(toplamTutar)} TL</p>
            </div>
          </div>
        </div>

        <div className="px-8 pb-8 pt-4 bg-gray-50/50 border-t border-gray-100">
          <button onClick={handleKaydet} disabled={kaydediliyor}
            className="w-full h-14 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 disabled:opacity-50 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest shadow-xl shadow-blue-100 transition-all active:scale-95 flex items-center justify-center gap-2">
            {kaydediliyor ? '⌛ İşlem Yapılıyor...' : (editPlan ? '💾 Değişiklikleri Kaydet' : '💾 Planı Oluştur')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Ödeme Planı Detay Modal ───────────────────────────────
function OdemePlaniModal({ plan, onClose, onOdendi }) {
  var [uyariAc, setUyariAc] = useState(false);
  var [bankalar, setBankalar] = useState([]);
  var [odemeTipleri, setOdemeTipleri] = useState([]);
  var [odemeTipi, setOdemeTipi] = useState('');
  var [bankaAdi, setBankaAdi] = useState('');

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  useEffect(() => {
    apiRequest('bankatanimlari').then(d => setBankalar(d || [])).catch(() => { });
    apiRequest('odemetipleri').then(d => {
      setOdemeTipleri(d || []);
      if (d && d.length > 0) setOdemeTipi(d[0].ad);
    }).catch(() => { });
  }, []);

  var odenenler = plan.taksitDetaylar.filter(t => t.odendi);
  var odenmemisler = plan.taksitDetaylar.filter(t => !t.odendi);
  var odenenTutarTotal = odenenler.reduce((s, t) => s + t.tutar, 0);
  var bekleyenTutarTotal = odenmemisler.reduce((s, t) => s + t.tutar, 0);
  var toplamTutarTotal = plan.taksitDetaylar.reduce((s, t) => s + t.tutar, 0);

  return createPortal(
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[9999] flex items-center justify-center p-4 animate-fadeIn" onClick={onClose}>
      <div className="bg-[#f8fafc] w-full max-w-6xl flex flex-col rounded-[3rem] overflow-hidden" style={{ maxHeight: '96dvh' }} onClick={e => e.stopPropagation()}>

        <div className="flex items-center justify-between px-4 sm:px-8 py-4 sm:py-6 border-b border-gray-100 bg-white sticky top-0 z-10 shadow-sm gap-3">
          <div className="flex items-center gap-3 sm:gap-5 min-w-0">
            <div className="w-10 h-10 sm:w-14 sm:h-14 flex-shrink-0 bg-gradient-to-br from-indigo-500 to-blue-700 rounded-2xl flex items-center justify-center text-white text-xl sm:text-2xl shadow-xl shadow-indigo-100">🧾</div>
            <div className="text-left font-mono min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-base sm:text-xl font-black text-gray-900 tracking-tighter uppercase leading-none">Ödeme Planı Detay</h2>
                <StatusBadge durum={bekleyenTutarTotal === 0 ? 'Kapandı' : plan.planDurum} />
              </div>
              <p className="text-[10px] font-black text-gray-400 mt-1.5 uppercase tracking-widest truncate">
                {plan.adSoyad} · Oda {plan.odaNo || '—'} · Rez #{plan.rezNo}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-gray-400 hover:text-gray-900 hover:bg-gray-50 rounded-2xl transition-all text-2xl sm:text-3xl font-black">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-6 md:p-10">
          <div className="max-w-6xl mx-auto space-y-10">

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                { label: 'TOPLAM PLAN', val: fmtPara(toplamTutarTotal), icon: '💰', color: 'from-blue-500 to-indigo-600', shadow: 'shadow-blue-100' },
                { label: 'TAHSİL EDİLEN', val: fmtPara(odenenTutarTotal), icon: '✅', color: 'from-emerald-500 to-teal-600', shadow: 'shadow-emerald-100' },
                { label: 'KALAN BORÇ', val: fmtPara(bekleyenTutarTotal), icon: '⏳', color: 'from-orange-400 to-rose-500', shadow: 'shadow-orange-100' }
              ].map(k => (
                <div key={k.label} className={`bg-gradient-to-br ${k.color} rounded-[2rem] p-6 text-white ${k.shadow} shadow-xl relative overflow-hidden group`}>
                  <div className="absolute right-0 bottom-0 text-6xl opacity-10 group-hover:scale-125 transition-transform">{k.icon}</div>
                  <p className="text-[9px] font-black uppercase tracking-widest opacity-70 mb-1">{k.label}</p>
                  <p className="text-2xl font-black tracking-tighter">{k.val} <span className="text-xs uppercase opacity-70">TL</span></p>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-6 md:p-8 space-y-8">
              <div className="flex flex-col md:flex-row items-center gap-6 pb-8 border-b border-gray-50 text-left">
                <div className="flex-1 w-full">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5 px-1">Ödeme Yöntemi</label>
                  <div className="flex bg-gray-50 p-1 rounded-2xl h-[56px] w-full max-w-lg overflow-x-auto custom-scrollbar no-scrollbar-desktop">
                    {odemeTipleri.map(t => (
                      <button key={t.odemeTipId} onClick={() => { setOdemeTipi(t.ad); setBankaAdi(''); }}
                        className={`flex-1 min-w-[100px] px-4 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${odemeTipi?.toLocaleLowerCase('tr') === t.ad.toLocaleLowerCase('tr') ? 'bg-white text-blue-600 shadow-sm border border-gray-100' : 'text-gray-500 hover:text-gray-700'
                          }`}>
                        {t.ad}
                      </button>
                    ))}
                  </div>
                </div>
                {odemeTipi?.toLocaleLowerCase('tr').includes('kredi') || odemeTipi?.toLocaleLowerCase('tr').includes('banka') || odemeTipi?.toLocaleLowerCase('tr').includes('havale') ? (
                  <div className="flex-1 w-full animate-fadeIn">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5 px-1">Banka Seçimi</label>
                    <select value={bankaAdi} onChange={e => setBankaAdi(e.target.value)}
                      className="w-full border-2 border-gray-100 rounded-2xl px-5 py-3.5 text-xs font-bold bg-white focus:outline-none focus:border-blue-400 uppercase tracking-widest transition-colors shadow-sm cursor-pointer">
                      <option value="">Seçiniz</option>
                      {bankalar.map(b => <option key={b.bankaTanimiId} value={b.ad}>{b.ad}</option>)}
                    </select>
                  </div>
                ) : null}
              </div>

              <div className="overflow-hidden">
                <div className="lg:block hidden">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-gray-50">
                        <th className="px-6 py-4 text-[10px] font-black text-gray-300 uppercase tracking-widest">No</th>
                        <th className="px-6 py-4 text-[10px] font-black text-gray-300 uppercase tracking-widest">Vade Tarihi</th>
                        <th className="px-6 py-4 text-[10px] font-black text-gray-300 uppercase tracking-widest text-right">Tutar</th>
                        <th className="px-6 py-4 text-[10px] font-black text-gray-300 uppercase tracking-widest">Durum</th>
                        <th className="px-6 py-4 text-[10px] font-black text-gray-300 uppercase tracking-widest">Ödeme Tarihi</th>
                        <th className="px-6 py-4 text-[10px] font-black text-gray-300 uppercase tracking-widest text-center">İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plan.taksitDetaylar.map(t => (
                        <tr key={t.taksitDetayId} className={`border-b border-gray-50 transition-all group ${t.odendi ? 'bg-emerald-50/20' : 'hover:bg-gray-50/50'}`}>
                          <td className="px-6 py-5 text-xs font-black text-gray-400 uppercase tracking-widest">{t.taksitNo}. Taksit</td>
                          <td className="px-6 py-5 text-sm font-black text-gray-700 tracking-tight leading-none">{fmtTarih(t.vadeTarihi)}</td>
                          <td className="px-6 py-5 text-sm font-black text-right tracking-tight">{fmtPara(t.tutar)} TL</td>
                          <td className="px-6 py-5">
                            <span className={`text-[9px] font-black px-3 py-1.5 rounded-xl uppercase tracking-widest border ${t.odendi ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-orange-50 text-orange-600 border-orange-100'
                              }`}>
                              {t.odendi ? 'Ödendi' : 'Ödenmedi'}
                            </span>
                          </td>
                          <td className="px-6 py-5 text-xs font-bold text-gray-400">{t.odemeTarihi ? fmtTarih(t.odemeTarihi) : '—'}</td>
                          <td className="px-6 py-5 text-center">
                            {!t.odendi && (
                              <button onClick={() => {
                                if (plan.checkOutYapildi || plan.planDurum === 'İptal') setUyariAc(true);
                                else onOdendi(t.taksitDetayId, odemeTipi, bankaAdi);
                              }}
                                className="px-6 py-2.5 bg-gradient-to-r from-orange-400 to-rose-500 hover:from-orange-500 hover:to-rose-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-orange-100 transition-all active:scale-95 leading-none">
                                Tahsil Et
                              </button>
                            )}
                            {t.odendi && <span className="text-emerald-500 text-base">✓</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="block lg:hidden space-y-4">
                  {plan.taksitDetaylar.map(t => (
                    <div key={t.taksitDetayId} className={`p-5 rounded-[2rem] border-2 transition-all ${t.odendi ? 'bg-emerald-50/30 border-emerald-100' : 'bg-white border-gray-100'}`}>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{t.taksitNo}. Taksit</span>
                        <span className="text-base font-black text-gray-900 tracking-tight">{fmtPara(t.tutar)} TL</span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-4">
                        <span>Vade: <span className="text-gray-800">{fmtTarih(t.vadeTarihi)}</span></span>
                        {t.odemeTarihi && <span>Ödeme: <span className="text-emerald-600 font-black">{fmtTarih(t.odemeTarihi)}</span></span>}
                      </div>
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100/50">
                        <span className={`text-[9px] font-black px-1.5 py-1.5 rounded-xl uppercase tracking-widest ${t.odendi ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'
                          }`}> {t.odendi ? 'Ödendi' : 'Bekliyor'}</span>
                        {!t.odendi && (
                          <button onClick={() => {
                            if (plan.checkOutYapildi || plan.planDurum === 'İptal') setUyariAc(true);
                            else onOdendi(t.taksitDetayId, odemeTipi, bankaAdi);
                          }}
                            className="px-6 py-3 bg-gradient-to-r from-orange-400 to-rose-500 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow shadow-orange-100">
                            Ödeme Al
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {uyariAc && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[99999] flex items-center justify-center px-4 animate-scale-in" onClick={() => setUyariAc(false)}>
          <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-sm p-10 flex flex-col items-center gap-6 text-center relative overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="w-20 h-20 rounded-full bg-rose-100 flex items-center justify-center text-4xl shadow-inner relative z-10">⚠️</div>
            <div className="relative z-10">
              <p className="text-xl font-black text-gray-900 uppercase tracking-tighter mb-2">İşlem Kısıtlandı</p>
              <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest leading-relaxed">
                {plan.planDurum === 'İptal' ? 'Bu ödeme planı iptal edildiği için tahsilat yapılamaz.' : 'Bu rezervasyonun işlemleri kapandığı (Check OUT) için yeni ödeme girişi yapılamaz.'}
              </p>
            </div>
            <button onClick={() => setUyariAc(false)} className="w-full h-14 bg-gray-900 hover:bg-black text-white rounded-2xl text-[11px] font-black uppercase tracking-widest shadow-xl transition-all active:scale-95 leading-none relative z-10">Anladım</button>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}

function SortIcon({ field, sortKey, sortDir }) {
  if (sortKey !== field) return <span className="text-gray-300 ml-1 opacity-50">⇅</span>;
  return <span className="text-blue-500 ml-1 font-black">{sortDir === 'asc' ? '↑' : '↓'}</span>;
}

// ── Ana Bileşen ───────────────────────────────────────────
export default function TaksitOdemeler() {
  var { personel } = useAuth();
  var [planlar, setPlanlar] = useState([]);
  var [yukleniyor, setYukleniyor] = useState(true);

  var [durum, setDurum] = useState('Hepsi');
  var [search, setSearch] = useState('');
  var [sortKey, setSortKey] = useState('adSoyad');
  var [sortDir, setSortDir] = useState('asc');
  var [aktifSayfa, setAktifSayfa] = useState(1);
  var [sayfaBasi, setSayfaBasi] = useState(100);

  var [yeniPlanModal, setYeniPlanModal] = useState(false);
  var [detayModal, setDetayModal] = useState(null);
  var [onayModal, setOnayModal] = useState(null);
  var [editPlan, setEditPlan] = useState(null);

  const fetchPlanlar = useCallback(async () => {
    setYukleniyor(true);
    try {
      var data = await apiRequest('taksit-planlari');
      setPlanlar(data || []);
    } catch (e) { console.error('Taksit planları yüklenemedi:', e); }
    setYukleniyor(false);
  }, []);

  useEffect(() => { fetchPlanlar(); }, [fetchPlanlar]);

  var planBazliListe = useMemo(() => {
    return planlar.map(plan => {
      var taksitler = plan.taksitDetaylar || [];
      var odenenler = taksitler.filter(t => t.odendi);
      var odenenTutar = odenenler.reduce((s, t) => s + t.tutar, 0);
      return {
        ...plan,
        odenenTutar,
        kalanTutar: plan.toplamTutar - odenenTutar,
        toplamTaksitSayisi: taksitler.length,
        odenenTaksitSayisi: odenenler.length,
        tamamlandi: odenenler.length === taksitler.length && taksitler.length > 0,
        progress: taksitler.length > 0 ? (odenenTutar / plan.toplamTutar) * 100 : 0
      };
    });
  }, [planlar]);

  var filtrelenmis = useMemo(() => {
    var liste = planBazliListe.filter(p => {
      if (durum === 'Ödendi' && !p.tamamlandi) return false;
      if (durum === 'Ödenmedi' && p.tamamlandi) return false;
      if (search && !p.adSoyad.toLocaleLowerCase('tr-TR').includes(search.toLocaleLowerCase('tr-TR')) && !p.rezNo.toLocaleLowerCase('tr-TR').includes(search.toLocaleLowerCase('tr-TR')) && !(p.odaNo || '').toString().includes(search)) return false;
      return true;
    });
    if (!sortKey) return liste;
    return liste.slice().sort((a, b) => {
      if (sortKey === 'toplamTutar') return sortDir === 'asc' ? a.toplamTutar - b.toplamTutar : b.toplamTutar - a.toplamTutar;
      if (sortKey === 'progress') return sortDir === 'asc' ? a.progress - b.progress : b.progress - a.progress;
      var va = (a[sortKey] || '').toString(), vb = (b[sortKey] || '').toString();
      return sortDir === 'asc' ? va.localeCompare(vb, 'tr') : vb.localeCompare(va, 'tr');
    });
  }, [planBazliListe, durum, search, sortKey, sortDir]);

  var filtrelenmisPage = filtrelenmis.slice((aktifSayfa - 1) * sayfaBasi, aktifSayfa * sayfaBasi);

  var totalPlanTutar = planBazliListe.reduce((s, p) => s + p.toplamTutar, 0);
  var totalOdenen = planBazliListe.reduce((s, p) => s + p.odenenTutar, 0);
  var totalKalan = totalPlanTutar - totalOdenen;

  function handleSort(key) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }

  async function handleOdendi(taksitDetayId, odemeTipi = 'Nakit', bankaAdi = '') {
    try {
      await apiRequest('taksit-planlari/taksit/' + taksitDetayId + '/odendi', 'PATCH', {
        odemeTarihi: (() => { const n = new Date(); return `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}T${pad(n.getHours())}:${pad(n.getMinutes())}:${pad(n.getSeconds())}`; })(),
        odemeTipi,
        bankaAdi,
        personelId: personel?.personelId || null,
      });
      await fetchPlanlar();
    } catch { alert('İşlem başarısız.'); }
  }

  async function handleSilOk(plan) {
    try {
      await apiRequest('taksit-planlari/' + plan.taksitPlaniId, 'DELETE');
      setOnayModal(null);
      fetchPlanlar();
    } catch (e) { alert(e.message || 'Silinemedi.'); }
  }

  async function handleIptalOk(plan) {
    try {
      await apiRequest(`taksit-planlari/${plan.taksitPlaniId}/iptal`, 'PATCH');
      setOnayModal(null);
      fetchPlanlar();
    } catch (e) { alert(e.message || 'İptal edilemedi.'); }
  }

  return (
    <div className="p-4 md:p-8 space-y-8 min-h-screen bg-[#f8fafc]/50 animate-fadeIn text-left" style={{ transform: 'translateZ(0)' }}>

      <div className="relative overflow-hidden bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-5 sm:p-8">
        <div className="absolute top-0 right-0 -mr-24 -mt-24 w-72 h-72 bg-indigo-50 rounded-full blur-3xl opacity-60 pointer-events-none transform-gpu animate-slow-pulse"></div>

        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-8 leading-none">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-blue-700 rounded-3xl flex items-center justify-center text-white text-4xl shadow-2xl shadow-indigo-200">🏦</div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tighter uppercase leading-none font-mono">Taksit Planları</h1>
              <p className="text-[11px] font-bold text-gray-400 mt-3 uppercase tracking-[0.2em] opacity-80">Ödeme planlarını yönet ve takip et</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => downloadExcel('taksit-planlari', 'Taksit Planları',
                ['Rez. No', 'Misafir', 'Oda', 'Toplam Tutar', 'Ödenen', 'Kalan', 'Durum'],
                filtrelenmis.map(p => [
                  p.rezNo, p.adSoyad, p.odaNo || '—',
                  p.toplamTutar.toFixed(2), p.odenenTutar.toFixed(2),
                  (p.toplamTutar - p.odenenTutar).toFixed(2),
                  p.tamamlandi ? 'Tamamlandı' : 'Devam Ediyor'
                ])
              )}
              className="flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-5 rounded-[2rem] text-[11px] font-black shadow-xl shadow-emerald-100 transition-all hover:-translate-y-1 active:scale-95 uppercase tracking-widest h-16">
              <span className="text-lg">📊</span>
              <span>EXCEL</span>
            </button>
            <button
              onClick={() => { setEditPlan(null); setYeniPlanModal(true); }}
              className="group relative flex items-center justify-center gap-4 bg-gradient-to-r from-indigo-600 to-blue-700 hover:from-indigo-700 hover:to-blue-800 text-white px-10 py-5 rounded-[2rem] text-[11px] font-black shadow-2xl shadow-indigo-200 transition-all hover:-translate-y-1 active:scale-95 uppercase tracking-widest overflow-hidden h-16">
              <span className="relative text-2xl transition-transform group-hover:rotate-90">＋</span>
              <span className="relative">YENİ ÖDEME PLANI</span>
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: 'TOPLAM HACİM', val: fmtPara(totalPlanTutar), icon: '📈', color: 'from-indigo-600 to-blue-700 shadow-indigo-100' },
          { label: 'TOPLAM TAHSİLAT', val: fmtPara(totalOdenen), icon: '✅', color: 'from-emerald-500 to-teal-600 shadow-emerald-100' },
          { label: 'BEKLEYEN ALACAK', val: fmtPara(totalKalan), icon: '⏳', color: 'from-orange-400 to-rose-500 shadow-orange-100' },
          { label: 'PLAN SAYISI', val: planBazliListe.length, icon: '📋', color: 'from-slate-700 to-slate-900 shadow-slate-100' }
        ].map((k, i) => (
          <div key={i} className={`relative overflow-hidden bg-gradient-to-br ${k.color} rounded-[2.5rem] p-7 text-white shadow-xl hover:shadow-2xl transition-all duration-500 group`}>
            <div className="absolute -right-4 -bottom-4 text-7xl opacity-10 pointer-events-none group-hover:scale-125 transition-transform duration-700 transform-gpu">{k.icon}</div>
            <p className="text-[9px] font-black uppercase tracking-widest opacity-70 mb-2">{k.label}</p>
            <p className="text-2xl md:text-3xl font-black tracking-tight leading-none">{k.val}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-[3rem] border border-gray-100 shadow-sm overflow-hidden flex flex-col min-h-[600px]">
        <div className="px-4 sm:px-8 py-5 sm:py-8 border-b border-gray-50 bg-[#f8fafc]/30 flex flex-col md:flex-row gap-6 sm:gap-8">
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-end">
            <div className="relative group">
              <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-6 bg-white px-2 z-10">Durum Filtresi</label>
              <select value={durum} onChange={e => { setDurum(e.target.value); setAktifSayfa(1); }} className="w-full border-2 border-gray-100 rounded-2xl px-6 py-4 text-[10px] font-black uppercase tracking-widest bg-white focus:outline-none focus:border-indigo-400 transition-colors shadow-sm appearance-none cursor-pointer">
                {['Hepsi', 'Ödendi', 'Ödenmedi', 'İptal'].map(d => <option key={d}>{d}</option>)}
              </select>
            </div>
            <div className="relative group">
              <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-6 bg-white px-2 z-10">Hızlı Arama</label>
              <input value={search} onChange={e => { setSearch(e.target.value); setAktifSayfa(1); }} placeholder="Misafir, Oda veya Rez..."
                className="w-full border-2 border-gray-100 rounded-2xl px-6 py-4 text-[10px] font-black uppercase tracking-widest bg-white focus:outline-none focus:border-indigo-400 transition-colors shadow-sm" />
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-8 flex-1 bg-white leading-none text-left">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:hidden">
            {yukleniyor ? (
              <LoadingSpinner fullPage text="Yükleniyor..." />
            ) : filtrelenmis.length === 0 ? (
              <div className="col-span-full py-20 text-center border-2 border-dashed border-gray-100 rounded-[3rem]">
                <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest italic">Kayıt bulunamadı</p>
              </div>
            ) : filtrelenmisPage.map(p => (
              <TaksitPlanKart key={p.taksitPlaniId} p={p}
                onDetay={plan => setDetayModal(plan)}
                onDuzenle={plan => { setEditPlan(plan); setYeniPlanModal(true); }}
                onIptal={plan => setOnayModal({ type: 'iptal', plan })}
                onSil={plan => setOnayModal({ type: 'sil', plan })} />
            ))}
          </div>

          <div className="hidden lg:block overflow-x-auto text-left">
            <table className="w-full text-left leading-none border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-100">
                  {[
                    { key: 'adSoyad', label: 'Misafir & Oda' },
                    { key: 'planDurum', label: 'Durum' },
                    { key: 'toplamTutar', label: 'Toplam', align: 'right' },
                    { key: 'kalanTutar', label: 'Kalan', align: 'right' },
                    { key: 'progress', label: 'İlerleme' },
                    { key: 'aks', label: 'Aksiyon', noSort: true, align: 'center' }
                  ].map(col => (
                    <th key={col.key}
                      onClick={() => !col.noSort && handleSort(col.key)}
                      className={`px-4 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest ${col.noSort ? '' : 'hover:bg-gray-50 cursor-pointer transition-colors'} ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'}`}>
                      {col.label} {!col.noSort && <SortIcon field={col.key} sortKey={sortKey} sortDir={sortDir} />}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-xs font-bold text-gray-700">
                {yukleniyor ? (
                  <tr><td colSpan={6} className="text-center py-20 font-black text-[10px] uppercase tracking-widest text-gray-400">⌛ Yükleniyor...</td></tr>
                ) : filtrelenmis.length === 0 ? (
                  <EmptyState as="td" colSpan={6} text="📭 Kayıt Bulunamadı" />
                ) : filtrelenmisPage.map(p => {
                  var isCancelled = p.planDurum === 'İptal';
                  return (
                    <tr key={p.taksitPlaniId} className={`border-b border-gray-50 hover:bg-gray-50/50 transition-all group ${p.tamamlandi ? 'bg-emerald-50/10' : ''} ${isCancelled ? 'opacity-60' : ''}`}>
                      <td className="px-4 py-6 cursor-pointer" onClick={() => setDetayModal(p)}>
                        <p className="font-black text-gray-900 tracking-tight leading-none group-hover:text-indigo-600 transition-colors uppercase">{p.adSoyad}</p>
                        <p className="text-[9px] font-bold text-gray-300 mt-1 uppercase tracking-widest font-mono">#{p.rezNo} · Oda {p.odaNo || '—'}</p>
                      </td>
                      <td className="px-4 py-6">
                        <StatusBadge durum={p.tamamlandi ? 'Kapandı' : p.planDurum} />
                      </td>
                      <td className="px-4 py-6 text-right font-black text-gray-900 text-sm whitespace-nowrap font-mono">{fmtPara(p.toplamTutar)} TL</td>
                      <td className="px-4 py-6 text-right font-black text-rose-500 text-sm whitespace-nowrap font-mono">{fmtPara(p.kalanTutar)} TL</td>
                      <td className="px-4 py-6 w-56">
                        <div className="flex items-center justify-between mb-1.5 text-[9px] font-black uppercase">
                          <span className={p.tamamlandi ? 'text-emerald-500' : 'text-blue-500'}>{p.progress.toFixed(0)}% Tamamlandı</span>
                        </div>
                        <ProgressBar percent={p.progress} color={p.tamamlandi ? 'bg-emerald-500' : isCancelled ? 'bg-rose-400' : 'bg-indigo-600'} />
                      </td>
                      <td className="px-4 py-6">
                        <div className="flex items-center justify-center gap-2 flex-wrap">
                          <button onClick={() => setDetayModal(p)} className="px-3 py-1.5 rounded-xl bg-gray-50 hover:bg-indigo-600 text-gray-400 hover:text-white text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 border border-gray-100">Detay</button>
                          {!isCancelled && <EditButton onClick={() => { setEditPlan(p); setYeniPlanModal(true); }} />}
                          {!isCancelled && <button onClick={() => setOnayModal({ type: 'iptal', plan: p })} className="px-3 py-1.5 rounded-xl bg-orange-50 hover:bg-orange-500 text-orange-500 hover:text-white text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 border border-orange-100">İptal</button>}
                          <DeleteButton onClick={() => setOnayModal({ type: 'sil', plan: p })} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination toplam={filtrelenmis.length} sayfaBasi={sayfaBasi} sayfaBasiDegistir={setSayfaBasi} aktifSayfa={aktifSayfa} sayfaDegistir={setAktifSayfa} />
        </div>
      </div>

      <UsageInfo
        color="indigo"
        description="Bu ekranda taksit planlarını yönetebilir, hatalı girişleri silebilir veya güncelleyebilirsiniz."
        scenarios={[
          { title: "İptal İşlemi", content: "Bir plan iptal edildiğinde yeni tahsilat yapılamaz ancak geçmiş ödeme kayıtları korunur." },
          { title: "Smart Düzenleme", content: "Ödemesi alınmış planlarda tutar değişikliği yapıldığında, kalan borç otomatik olarak ileriki taksitlere yansıtılır." }
        ]}
      />

      {yeniPlanModal && (
        <YeniPlanModal onClose={() => { setYeniPlanModal(false); setEditPlan(null); }} onKaydedildi={fetchPlanlar} editPlan={editPlan} />
      )}

      {detayModal && (
        <OdemePlaniModal plan={detayModal} onClose={() => setDetayModal(null)} onOdendi={async (id, tip, banka) => {
          await handleOdendi(id, tip, banka);
          var data = await apiRequest('taksit-planlari');
          setPlanlar(data || []);
          var guncel = (data || []).find(p => p.taksitPlaniId === detayModal.taksitPlaniId);
          if (guncel) setDetayModal(guncel);
        }} />
      )}

      {onayModal && (
        <OnayModal
          baslik={onayModal.type === 'iptal' ? 'Planı İptal Et' : 'Planı Sil'}
          icerik={`${onayModal.plan.adSoyad} misafirine ait bu planı ${onayModal.type === 'iptal' ? 'iptal etmek' : 'tamamen silmek'} istediğinize emin misiniz?`}
          tehlikeli={true}
          onOnay={() => onayModal.type === 'iptal' ? handleIptalOk(onayModal.plan) : handleSilOk(onayModal.plan)}
          onIptal={() => setOnayModal(null)}
        />
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
        .no-scrollbar-desktop::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}
